/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Why: these values were redeclared in ~12 files with two spellings of the same
 * number (`5 * 60 * 1000` and `300_000`), so changing the cache policy required
 * touching every site and risked leaving values divergent. Import from here
 * instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving all other durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by foreground reminders window.*/
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale data.
 * It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although it currently matches the offline
 * cache value) because it is an independent decision: it must stay aligned
 * across `useSession`, the `_app` route guard, and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Ceiling for waiting on a `get-session` made INSIDE a route `beforeLoad`.
 *
 * P23/CC-60 · I1-bis: a `beforeLoad` that `await`s the network without a ceiling does not fail
 * with 401 or 503 (both resolve fast) — fails when response
 * STALLS: router does not mount route, `<Suspense fallback={null}>` of
 * `__root` has nothing to render and whole app goes BLANK for as long
 * as network takes. Measured in this repo, on `/login`: `innerText`
 * frame at 3, 6, 10, 15 and 20 s, on both dev server and production
 * bundle.
 *
 * SINGLE source for both route guards (`_app` and `_auth`): having it in a
 * LOCAL literal within `_app` was exactly what left `_auth` without defense
 * that `_app` had, which is why I1 closed green measuring `/`
 * (healthy) instead of `/login` (broken). Past ceiling screen is painted and when
 * response arrives it synchronizes machine/cache — `BootManager`
 * redirects later if session existed.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * `staleTime` of enabled social providers catalog.
 *
 * Changes very rarely, so cached aggressively. Shared
 * constant (P23/CC-60 · P26) because SAME `queryKey`
 * (`auth.socialProviders`) is consumed by two distinct components —
 * `auth/social-auth-buttons.tsx` and `profile/connected-accounts-section.tsx`— and
 * diverging in cache or error policy causes one to poison the other.
 */
export const PROVIDERS_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Prefix of `queryKey` for profile active sessions list.
 *
 * The real query is declared as `[...ACTIVE_SESSIONS_QUERY_KEY, sessionId]`, and
 * whatever invalidates it (revocation in `BootManager`) uses PREFIX because it does not
 * know tab `sessionId`. Lives here so it does not become duplicate
 * literal across two disjoint files (P23/CC-60 · P25).
 */
export const ACTIVE_SESSIONS_QUERY_KEY = ["active-sessions"] as const;

/**
 * Debounce for text inputs that persist while typing (title and notes in
 * detail, subtasks). Balances typing responsiveness with mutation volume.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably greater than the time the browser takes to consume
 * the `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
